import Navigation from "@/components/Navigation";
import HeroBanner from "@/components/HeroBanner";
import PricingCTA from "@/components/PricingCTA";
import FeaturedBillboards from "@/components/FeaturedBillboards";
import FeaturedPackages from "@/components/FeaturedPackages";
import MillionViewInventories from "@/components/MillionViewInventories";
import ConvenienceUSP from "@/components/ConvenienceUSP";
import CoverageUSP from "@/components/CoverageUSP";
import ROIUSP from "@/components/ROIUSP";
import ClientsServed from "@/components/ClientsServed";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer1";
import { useEffect, useState } from "react";
import type { Configuration } from "@/types/config";
import { normalizeDefault } from "@/utils/validationUtils";
import SustainabilitySection from "@/components/SustainabilitySection";
import { useGeolocation } from '@/utils/simpleGeolocation';
import CountrySelectionModal from '@/components/CountrySelectionModal';
import { useQuery } from "@apollo/client";
import { GET_MARKETPLACE_COUNTRIES } from "@/Graphql/Queries";

interface LandingProps {
  yourLogo: string;
  footerLogo: string;
  currencyCode: string;
  countryId: string;
  countryName: string;
  base_path: string;
  exposure: string;
  isExploreEnabled?: boolean;
  isLoginEnabled?: boolean;
  configData?: any[];
}

export default function Landing({
  yourLogo,
  footerLogo,
  currencyCode,
  countryId,
  countryName,
  base_path,
  exposure,
  isExploreEnabled = true,
  isLoginEnabled = true,
  configData = []
}: LandingProps) {

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [localConfigData, setLocalConfigData] = useState<any[]>(configData || []);
  const [contentData, setContentData] = useState<any[]>([]);

  // Use props instead of local state for configuration
  const finalIsExploreEnabled = isExploreEnabled;
  const finalIsLoginEnabled = isLoginEnabled;

  // Simple geolocation on page load
  const { result: geoResult } = useGeolocation();

  // Country list query for popup
  const { data: countryData, loading: countryLoading } = useQuery(GET_MARKETPLACE_COUNTRIES, {
    variables: { exposure: exposure }
  });

  // Country selection popup state
  const [showCountryPopup, setShowCountryPopup] = useState(false);
  const [selectedCountryFromPopup, setSelectedCountryFromPopup] = useState<any>(null);

  // Log geolocation result and handle country selection popup
  useEffect(() => {
    if (geoResult && countryData?.getMarketplaceCountries) {
      console.log('🌍 Landing - Geolocation result:', geoResult);

      // Check if user has already selected a country in this session
      const hasSessionDefault = localStorage.getItem('sessionDefaultCountryId');

      // If session default exists, skip everything
      if (hasSessionDefault) {
        console.log('✅ Session country already set, skipping popup');
        return;
      }

      // Check if geolocation succeeded
      if (geoResult.success && geoResult.country) {
        console.log(`✅ Landing - User location: ${geoResult.country} (IP: ${geoResult.ipAddress})`);

        // Check if detected country is available in our list
        const countryOptions = countryData.getMarketplaceCountries || [];
        const searchCountry = geoResult.country.toLowerCase().trim();

        // Try exact match first
        let availableCountry = countryOptions.find((c: any) =>
          c.name?.toLowerCase().trim() === searchCountry
        );

        // If no exact match, try partial match
        if (!availableCountry) {
          availableCountry = countryOptions.find((c: any) => {
            const countryName = c.name?.toLowerCase().trim() || '';
            return countryName.includes(searchCountry) || searchCountry.includes(countryName);
          });
        }

        if (availableCountry) {
          // ✅ Detected country IS in the list - Auto-select it
          console.log('✅ IP country is available:', availableCountry.name);
          console.log('🆔 Country ID:', availableCountry.id);

          // Auto-save both IDs
          localStorage.setItem('sessionDefaultCountryId', availableCountry.id);
          localStorage.setItem('selectedCountryId', availableCountry.id);
          localStorage.setItem('sessionDefaultCountryName', availableCountry.name);

          console.log('💾 Auto-selected country (IP detection):', availableCountry.name);
          setSelectedCountryFromPopup(availableCountry);

          // Mark that we've handled this automatically
          sessionStorage.setItem('countryPopupShown', 'true');
        } else {
          // ❌ Detected country is NOT in the list - Show manual selection modal
          console.log('❌ IP country not available in our service area:', geoResult.country);
          console.log('🔔 Available countries:', countryOptions.map((c: any) => c.name).join(', '));
          console.log('🔔 Showing country selection popup for manual selection...');
          setShowCountryPopup(true);
        }
      } else {
        // Geolocation failed - Show manual selection modal
        console.warn('⚠️ Landing - Geolocation failed:', geoResult.error || geoResult.message);
        console.log('🔔 Showing country selection popup due to geolocation failure...');
        setShowCountryPopup(true);
      }
    }
  }, [geoResult, countryData]);

  // Handle country selection from modal
  const handleCountrySelect = (country: any) => {
    console.log('🌍 User selected country from modal:', country.name);
    setSelectedCountryFromPopup(country);

    // ✅ CRITICAL FIX: Save both IDs to ensure explore page works correctly
    localStorage.setItem('sessionDefaultCountryId', country.id);
    localStorage.setItem('selectedCountryId', country.id); // Save to selectedCountryId for immediate explore page access
    localStorage.setItem('sessionDefaultCountryName', country.name);

    // ✅ Mark popup as shown in this session (not localStorage, so it shows after logout)
    sessionStorage.setItem('countryPopupShown', 'true');

    console.log('💾 Saved country to localStorage:', {
      sessionDefaultCountryId: country.id,
      selectedCountryId: country.id,
      countryName: country.name
    });
    setShowCountryPopup(false);
  };

  // Use config data passed from App.tsx
  useEffect(() => {
    if (configData && configData.length > 0) {
      setLocalConfigData(configData);
      setLoadingConfig(false);
    }
  }, [configData]);

  const isFieldEnabled = (key: string, config: any[]) => {
    const landingPageConfig = config?.find((page: { name: string }) => page.name === "Landing Page");
    const configdata = landingPageConfig?.configurations || [];
    const fieldConfig = configdata?.find((field: any) => field.key === key);
    return fieldConfig ? normalizeDefault(fieldConfig.default) : true;
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;

        const response = await fetch(url);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error("Non-JSON response:", text);
          throw new Error("Response is not JSON");
        }

        const data = await response.json();
        setContentData(data);

        const pageData = data.find((page: any) => page.name === "Landing Page");
        if (!pageData) {
          console.warn("Landing Page data not found");
          return;
        }
      } catch (error) {
        console.error("Error fetching content:", error);
      }
    };

    fetchContent();
  }, []);


  return (
    <div className="min-h-screen bg-warm-white">
      <Navigation
        configData={localConfigData}
        yourLogo={yourLogo}
        base_path={base_path}
        mode="landing"
      />
      <HeroBanner
        isExploreEnabled={finalIsExploreEnabled}
        isLoginEnabled={finalIsLoginEnabled}
        contentData={contentData}
        configData={localConfigData}
        base_path={base_path}
      />

      {/* Country Selection Modal */}
      <CountrySelectionModal
        open={showCountryPopup}
        onOpenChange={setShowCountryPopup}
        onCountrySelect={handleCountrySelect}
        detectedCountry={geoResult?.country}
        userIP={geoResult?.ipAddress}
        hasInventory={false}
        allowClose={true}
        exposure={exposure}
      />
      {isFieldEnabled('low_emission_high_impact', localConfigData) && (
        <SustainabilitySection base_path={base_path} isExploreEnabled={finalIsExploreEnabled} />
      )}
      {isFieldEnabled('revolutionary pricing model', localConfigData) && (
        <PricingCTA />
      )}
      {isFieldEnabled('featured packages', localConfigData) && (
        <FeaturedPackages
          base_path={base_path}
          countryId={countryId}
          currencyCode={currencyCode}
          isExploreEnabled={finalIsExploreEnabled}
          isLoginEnabled={finalIsLoginEnabled}
          exposure={exposure}
        />
      )}
      {isFieldEnabled('featured properties', localConfigData) && (
        <FeaturedBillboards
          base_path={base_path}
          countryId={countryId}
          currencyCode={currencyCode}
          isExploreEnabled={finalIsExploreEnabled}
          isLoginEnabled={finalIsLoginEnabled}
          exposure={exposure}
        />
      )}
      {isFieldEnabled('popular properties', localConfigData) && (
        <MillionViewInventories

          countryId={countryId}
          base_path={base_path}
          isExploreEnabled={finalIsExploreEnabled}
          isLoginEnabled={finalIsLoginEnabled}
          exposure={exposure}
        />
      )}
      {isFieldEnabled('speed&efficiency', localConfigData) && (
        <ConvenienceUSP
          isExploreEnabled={finalIsExploreEnabled}
          isLoginEnabled={finalIsLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('global coverage', localConfigData) && (
        <CoverageUSP
          isExploreEnabled={finalIsExploreEnabled}
          isLoginEnabled={finalIsLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('ROI optimization', localConfigData) && (
        <ROIUSP
          isExploreEnabled={finalIsExploreEnabled}
          isLoginEnabled={finalIsLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('clients we have served', localConfigData) && (
        <ClientsServed />
      )}
      {isFieldEnabled('clients testimonials', localConfigData) && (
        <Testimonials />
      )}
      {isFieldEnabled('faq', localConfigData) && (
        <FAQ />
      )}
      {isFieldEnabled('contact form', localConfigData) && (
        <ContactForm countryName={countryName}
          contentData={contentData}
          configData={localConfigData}
        />
      )}
      <Footer configData={localConfigData} yourLogo={footerLogo} base_path={base_path} />
    </div>
  );
}
